import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import type { Note, NoteCategory } from '@/core/models/note';
import { CATEGORY_LABEL } from '@/core/models/note';
import { cn } from '@/utils/cn';

const BAR: Record<NoteCategory, string> = {
  inspiration: 'from-violet-400/80 to-fuchsia-500/80',
  todo: 'from-sky-400/80 to-indigo-500/80',
  mood: 'from-rose-400/80 to-orange-400/80',
  memo: 'from-emerald-400/80 to-teal-500/80',
  review: 'from-amber-400/80 to-rose-500/80',
};

interface Props {
  note: Note;
  index?: number;
}

export function NoteCard({ note, index = 0 }: Props) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index, 10) * 0.03, duration: 0.25 }}
      whileHover={{ y: -2 }}
    >
      <Link
        to={`/notes/${note.id}`}
        className={cn(
          'group relative block overflow-hidden rounded-2xl border border-border bg-bg-elevated/80 backdrop-blur p-4',
          'hover:border-accent/40 transition-colors'
        )}
      >
        <div
          className={cn(
            'absolute left-0 top-0 h-full w-[3px] bg-gradient-to-b',
            BAR[note.category]
          )}
        />
        <div className="flex items-center gap-2 text-xs text-fg-subtle">
          <span className="rounded-md bg-bg px-2 py-0.5">
            {CATEGORY_LABEL[note.category] ?? note.category}
          </span>
          <span>{formatDate(note.created_at)}</span>
        </div>
        <h3 className="mt-2 text-base font-semibold line-clamp-1 group-hover:text-gradient transition-colors">
          {note.title || '(无标题)'}
        </h3>
        {note.content && (
          <p className="mt-1 text-sm text-fg-muted line-clamp-2">{note.content}</p>
        )}
        {note.tags && note.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {note.tags.slice(0, 4).map((t) => (
              <span key={t} className="text-xs text-fg-subtle">
                #{t}
              </span>
            ))}
          </div>
        )}
      </Link>
    </motion.div>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const same = d.toDateString() === now.toDateString();
    if (same) return d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' });
  } catch {
    return '';
  }
}
